import React from "react";
import { defineMessages, FormatDateOptions, useIntl } from "react-intl";

import { Sex } from "lib/build/concepts";
import { queries as ktxQueries } from "lib/build/dashboards/ktx/queries";
import { queries as sharedQueries } from "lib/build/dashboards/shared";
import { Event, EventQuery } from "lib/build/events";
import {
	Ab0,
	ab0Messages,
	Rh,
	rhMessages
} from "lib/build/forms/ktx/basic-registration-data/clinical-data/bloodGroup";
import {
	HasOrganTransplantation,
	hasOrganTransplantationMessages,
	OrganTransplantation,
	organTransplantationMessages
} from "lib/build/forms/ktx/basic-registration-data/clinical-data/pre-immunisation/combinedOrganTransplantation";
import { PreviousOtherOrganTransplantationType } from "lib/build/forms/ktx/basic-registration-data/clinical-data/pre-immunisation/previousOtherOrganTransplantation";
import {
	LiveDonorScreeningInProgress,
	liveDonorScreeningInProgressMessages
} from "lib/build/forms/ktx/basic-registration-data/liveDonorScreening";
import {
	CauseOfGraftloss,
	causeOfGraftlossMessages,
	hasGraftlossMessages
} from "lib/build/forms/ktx/basic-registration-data/waiting-list-data/graftloss";
import {
	DiagnosisBaselineDisease,
	diagnosisBaselineDiseaseMessages
} from "lib/build/forms/ktx/basic-registration-data/waiting-list-data/kidneyDiseaseEthiology";
import {
	RenalReplacementTherapyType,
	renalReplacementTherapyTypeMessages
} from "lib/build/forms/ktx/basic-registration-data/waiting-list-data/renalReplacementTherapy";
import { StatusByNephrologist } from "lib/build/forms/ktx/patientCondition";

import {
	useFormatMessageMap,
	useFormatMessageMapWithoutWarning
} from "src/common/hooks/useFormatMessageMap";
import { useFormatInitials } from "src/common/hooks/useFormatName";
import {
	useEventsByQueries,
	useEventsByQuery,
	useLastEventByQuery,
	useLastEventValueByQuery
} from "src/features/events";
import { orgMessages } from "src/messages/generic";
import { useDialysisCentre } from "src/modules/ktx/header/hooks/useDialysisCentre";
import { useSocialSecurityNumber } from "src/modules/ktx/header/hooks/useSocialSecurityNumber";
import { PDFData } from "src/modules/ktx/header/utils/generatePDF";

enum YesNo {
	YES = "YES",
	NO = "NO"
}

enum PosNeg {
	POSITIVE = "POSITIVE",
	NEGATIVE = "NEGATIVE"
}

const formatDateOptions: FormatDateOptions = {
	year: "numeric",
	month: "numeric",
	day: "numeric"
};

const yesNoMessages = defineMessages({
	[YesNo.YES]: {
		id: "ktx.pdf.yes",
		defaultMessage: "igen"
	},
	[YesNo.NO]: {
		id: "ktx.pdf.no",
		defaultMessage: "nem"
	}
});

const posNegMessages = defineMessages({
	[PosNeg.POSITIVE]: {
		id: "ktx.pdf.pos",
		defaultMessage: "pos"
	},
	[PosNeg.NEGATIVE]: {
		id: "ktx.pdf.neg",
		defaultMessage: "neg"
	}
});

const viralSelogoyQueries = [
	ktxQueries.hiv,
	ktxQueries.hbvStatus,
	ktxQueries.hcv,
	ktxQueries.cmv,
	ktxQueries.toxoplasma,
	ktxQueries.treponemaPallidum,
	ktxQueries.ebv
];

export function usePDFData(): PDFData {
	const registrationDate = useDateValue(ktxQueries.waitingListStartDate);
	const patientEtId = useStringValue(ktxQueries.patientEtId);
	const dialysisCentreCode = useDialysisCentreCode();
	const dialysisCentreName = useDialysisCentreName(dialysisCentreCode);
	const isFirstRegistration = useIsFirstRegistration();
	const isReRegistration = useIsReRegistration();

	const firstName = useStringValue(sharedQueries.firstName);
	const lastName = useStringValue(sharedQueries.lastName);
	const initials = useInitials();
	const dateOfBirth = useDateValue(sharedQueries.dateOfBirth);
	const sex = useSex();
	const address = useAddress();
	const socialSecurityNumber = useSocialSecurityNumber() || "";
	const phoneNumbers = useArrayValue<string>(sharedQueries.phoneNumber);
	const phoneNumber1 = phoneNumbers[0] || "";
	const phoneNumber2 = phoneNumbers[1] || "";
	const bloodGroupAb0 = useBloodGroupAb0();
	const bloodGroupRh = useBloodGroupRh();
	const weight = useNumberValue(ktxQueries.weight);
	const height = useNumberValue(ktxQueries.height);
	const transfusionBeforeRegistration = useTransfusionBeforeRegistration();
	const transfusionAfterRegistration = useTransfusionAfterRegistration();
	const pregnancies = useNumberProp(
		ktxQueries.pregnanciesAndChildren,
		"numberOfPregnancies"
	);
	const children = useNumberProp(
		ktxQueries.pregnanciesAndChildren,
		"numberOfChildren"
	);

	const viralSelogoyDate = useLastTimestamp(viralSelogoyQueries);
	const antiHiv = usePosNegProp(ktxQueries.hiv, "result");
	const hbsAg = usePosNegProp(ktxQueries.hbvStatus, "HBsAg");
	const antiHbs = usePosNegProp(ktxQueries.hbvStatus, "antiHBs");
	const antiHbc = usePosNegProp(ktxQueries.hbvStatus, "aHBctotal");
	const antiHcv = usePosNegProp(ktxQueries.hcv, "result");
	const antiCmvIgg = usePosNegProp(ktxQueries.cmv, "igG");
	const antiCmvIgm = usePosNegProp(ktxQueries.cmv, "igM");
	const toxoplasmaIgg = usePosNegProp(ktxQueries.toxoplasma, "igG");
	const treponemaPallidum = usePosNegProp(
		ktxQueries.treponemaPallidum,
		"result"
	);
	const hbImm = useYesNoProp(ktxQueries.hbvStatus, "HBImmun");
	const antiEbv = usePosNegProp(ktxQueries.ebv, "igG");

	const transplantable = useIsTransplantable();
	const notTransplantable = useIsNotTransplantable();
	const combinedTransplant = useCombinedTransplant();
	const liveDonor = useLiveDonor();
	const previousKidneyTransplantationDates =
		usePreviousKidneyTransplantationDates();
	const previousKidneyTransplantationDate1 =
		previousKidneyTransplantationDates[0] || "";
	const previousKidneyTransplantationDate2 =
		previousKidneyTransplantationDates[1] || "";
	const previousKidneyTransplantationDate3 =
		previousKidneyTransplantationDates[2] || "";
	const edta = useEdta();
	const renalReplacementTherapyDate = useRenalReplacementTherapyDate();
	const renalReplacementTherapyType = useRenalReplacementTherapyType();
	const causeOfGraftloss = useCauseOfGraftloss();
	const graftlossDate = useGraftlossDate();

	return React.useMemo(
		() => ({
			transplantCenter: "HBSTP",
			registrationDate,
			patientEtId,
			dialysisCentreCode,
			dialysisCentreName,
			isFirstRegistration,
			isReRegistration,

			firstName,
			lastName,
			initials,
			dateOfBirth,
			sex,
			citizenship: "HUN",
			address,
			socialSecurityNumber,
			phoneNumber1,
			phoneNumber2,
			bloodGroupAb0,
			bloodGroupRh,
			weight,
			height,
			transfusionBeforeRegistration,
			transfusionAfterRegistration,
			pregnancies,
			children,

			viralSelogoyDate,
			antiHiv,
			hbsAg,
			antiHbs,
			antiHbc,
			antiHcv,
			antiCmvIgg,
			antiCmvIgm,
			toxoplasmaIgg,
			treponemaPallidum,
			hbImm,
			antiEbv,

			transplantable,
			notTransplantable,
			combinedTransplant,
			liveDonor,
			previousKidneyTransplantationDate1,
			previousKidneyTransplantationDate2,
			previousKidneyTransplantationDate3,
			edta,
			renalReplacementTherapyDate,
			renalReplacementTherapyType,
			causeOfGraftloss,
			graftlossDate
		}),
		[
			registrationDate,
			patientEtId,
			dialysisCentreCode,
			dialysisCentreName,
			isFirstRegistration,
			isReRegistration,

			firstName,
			lastName,
			initials,
			dateOfBirth,
			sex,
			address,
			socialSecurityNumber,
			phoneNumber1,
			phoneNumber2,
			bloodGroupAb0,
			bloodGroupRh,
			weight,
			height,
			transfusionBeforeRegistration,
			transfusionAfterRegistration,
			pregnancies,
			children,

			viralSelogoyDate,
			antiHiv,
			hbsAg,
			antiHbs,
			antiHbc,
			antiHcv,
			antiCmvIgg,
			antiCmvIgm,
			toxoplasmaIgg,
			treponemaPallidum,
			hbImm,
			antiEbv,

			transplantable,
			notTransplantable,
			combinedTransplant,
			liveDonor,
			previousKidneyTransplantationDate1,
			previousKidneyTransplantationDate2,
			previousKidneyTransplantationDate3,
			edta,
			renalReplacementTherapyDate,
			renalReplacementTherapyType,
			causeOfGraftloss,
			graftlossDate
		]
	);
}

function useArrayValue<T>(query: EventQuery): T[] {
	const value = useLastEventValueByQuery<T[]>(query);
	return React.useMemo(() => value || [], [value]);
}

function useStringValue(query: EventQuery): string {
	const value = useLastEventValueByQuery<string>(query);
	return React.useMemo(() => value || "", [value]);
}

function useDateValue(query: EventQuery): string {
	const value = useLastEventValueByQuery<string>(query);
	const { formatDate } = useIntl();
	return React.useMemo(
		() => (value === undefined ? "" : formatDate(value, formatDateOptions)),
		[value, formatDate]
	);
}

function useNumberValue(query: EventQuery): string {
	const value = useLastEventValueByQuery<number>(query);
	return React.useMemo(
		() => (value === undefined ? "" : `${value}`),
		[value]
	);
}

function useNumberProp(query: EventQuery, key: string): string {
	const value = useLastEventValueByQuery<{ [key: string]: number }>(query);
	return React.useMemo(
		() =>
			value === undefined || value[key] === undefined
				? ""
				: `${value[key]}`,
		[value, key]
	);
}

function useYesNoProp(query: EventQuery, key: string): string {
	const value = useLastEventValueByQuery<{ [key: string]: YesNo }>(query);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() =>
			value === undefined || value[key] === undefined
				? ""
				: formatMessage(yesNoMessages, value[key]),
		[value, key, formatMessage]
	);
}

function usePosNegProp(query: EventQuery, key: string): string {
	const value = useLastEventValueByQuery<{ [key: string]: PosNeg }>(query);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() =>
			value === undefined || value[key] === undefined
				? ""
				: formatMessage(posNegMessages, value[key]),
		[value, key, formatMessage]
	);
}

function useLastTimestamp(queries: EventQuery[]): string {
	const events = useEventsByQueries(queries);
	const { formatDate } = useIntl();
	return React.useMemo(() => {
		const timestamps = events
			.filter(
				(event: Event | undefined): event is Event =>
					event !== undefined
			)
			.map(event => new Date(event.timestamp));
		if (timestamps.length === 0) {
			return "";
		}
		let maxTs = timestamps[0];
		for (let i = 1; i < timestamps.length; i++) {
			const ts = timestamps[i];
			if (ts > maxTs) {
				maxTs = ts;
			}
		}
		return formatDate(maxTs, formatDateOptions);
	}, [events, formatDate]);
}

function useDialysisCentreCode(): string {
	const data = useDialysisCentre();
	return React.useMemo(
		() => (data.orgInfo === undefined ? "" : data.orgInfo.code),
		[data]
	);
}

function useDialysisCentreName(code: string): string {
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() => (!code || code === "" ? "" : formatMessage(orgMessages, code)),
		[code, formatMessage]
	);
}

function useIsFirstRegistration(): boolean {
	const events = useEventsByQuery<PreviousOtherOrganTransplantationType>(
		ktxQueries.previousOtherOrganTransplantation
	);
	return React.useMemo(
		() =>
			events.length === 0 ||
			events.every(
				({ value }) =>
					value !==
					PreviousOtherOrganTransplantationType.PREVIOUS_TRANSPLANTATION_KIDNEY
			),

		[events]
	);
}

function useIsReRegistration(): boolean {
	const events = useEventsByQuery<PreviousOtherOrganTransplantationType>(
		ktxQueries.previousOtherOrganTransplantation
	);
	return React.useMemo(
		() =>
			events.length !== 0 &&
			events.some(
				({ value }) =>
					value ===
					PreviousOtherOrganTransplantationType.PREVIOUS_TRANSPLANTATION_KIDNEY
			),
		[events]
	);
}

function useInitials(): string {
	const firstName = useLastEventValueByQuery<string>(sharedQueries.firstName);
	const lastName = useLastEventValueByQuery<string>(sharedQueries.lastName);
	const formatInitials = useFormatInitials();
	return React.useMemo(
		() =>
			firstName === undefined || lastName === undefined
				? ""
				: formatInitials({ firstName, lastName }),

		[firstName, lastName, formatInitials]
	);
}

function useSex(): string {
	const value = useLastEventValueByQuery<Sex>(sharedQueries.sex);
	return React.useMemo(() => {
		if (value === Sex.Male) {
			return "M";
		}
		if (value === Sex.Female) {
			return "F";
		}
		return "";
	}, [value]);
}

function useAddress(): string {
	const value = useLastEventValueByQuery<{
		postCode: string;
		city: string;
		addressLineOne: string;
		addressLineTwo?: string;
	}>(sharedQueries.address);
	return React.useMemo(() => {
		if (value === undefined) {
			return "";
		}
		const { postCode, city, addressLineOne, addressLineTwo = "" } = value;
		let address = `${postCode} ${city}, ${addressLineOne}`;
		if (addressLineTwo !== "") {
			address += `, ${addressLineTwo}`;
		}
		return address;
	}, [value]);
}

function useBloodGroupAb0(): string {
	const value = useLastEventValueByQuery<Ab0>(ktxQueries.vAb0);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() => (value === undefined ? "" : formatMessage(ab0Messages, value)),
		[value, formatMessage]
	);
}

function useBloodGroupRh(): string {
	const value = useLastEventValueByQuery<Rh>(ktxQueries.vRh);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() => (value === undefined ? "" : formatMessage(rhMessages, value)),
		[value, formatMessage]
	);
}

function useTransfusionBeforeRegistration(): string {
	const registrationDateValue = useLastEventValueByQuery<string>(
		ktxQueries.waitingListStartDate
	);
	const transfusionEvents = useEventsByQuery<{ transfusionOccurred: string }>(
		ktxQueries.transfusion
	);
	const { formatMessage } = useIntl();
	return React.useMemo(() => {
		if (transfusionEvents.length === 0) {
			return "";
		}

		let occurred: boolean;
		if (registrationDateValue === undefined) {
			occurred = transfusionEvents.some(
				event => event.value.transfusionOccurred === "YES"
			);
		} else {
			const registrationDate = new Date(registrationDateValue);
			occurred = transfusionEvents.some(
				event =>
					event.value.transfusionOccurred === "YES" &&
					new Date(event.timestamp) < registrationDate
			);
		}
		return formatMessage(
			occurred ? yesNoMessages[YesNo.YES] : yesNoMessages[YesNo.NO]
		);
	}, [registrationDateValue, transfusionEvents, formatMessage]);
}

function useTransfusionAfterRegistration(): string {
	const registrationDateValue = useLastEventValueByQuery<string>(
		ktxQueries.waitingListStartDate
	);
	const transfusionEvents = useEventsByQuery<{ transfusionOccurred: string }>(
		ktxQueries.transfusion
	);
	const { formatMessage } = useIntl();
	return React.useMemo(() => {
		if (transfusionEvents.length === 0) {
			return "";
		}

		if (registrationDateValue === undefined) {
			return "";
		}

		const registrationDate = new Date(registrationDateValue);
		const occurred = transfusionEvents.some(
			event =>
				event.value.transfusionOccurred === "YES" &&
				new Date(event.timestamp) > registrationDate
		);

		return formatMessage(
			occurred ? yesNoMessages[YesNo.YES] : yesNoMessages[YesNo.NO]
		);
	}, [registrationDateValue, transfusionEvents, formatMessage]);
}

function useIsTransplantable(): boolean {
	const value = useLastEventValueByQuery<StatusByNephrologist>(
		ktxQueries.statusByNephrologist
	);
	return value === StatusByNephrologist.TRANSPLANTABLE;
}

function useIsNotTransplantable(): boolean {
	const value = useLastEventValueByQuery<StatusByNephrologist>(
		ktxQueries.statusByNephrologist
	);
	return value === StatusByNephrologist.NOT_TRANSPLANTABLE;
}

function useCombinedTransplant(): string {
	const event = useLastEventByQuery<{
		hasOrganTransplantation: HasOrganTransplantation;
		organTransplantation: OrganTransplantation[];
	}>(ktxQueries.combinedOrganTransplantation);

	const formatMessage = useFormatMessageMap();
	return React.useMemo(() => {
		if (!event) {
			return "";
		}

		if (event.value.organTransplantation) {
			return Array.from(
				new Set(
					event.value.organTransplantation.map(t => {
						return formatMessage(organTransplantationMessages, t);
					})
				)
			).join(", ");
		}

		return formatMessage(
			hasOrganTransplantationMessages,
			event.value.hasOrganTransplantation
		);
	}, [event, formatMessage]);
}

function useLiveDonor(): string {
	const value = useLastEventValueByQuery<{
		liveDonorScreeningInProgress: LiveDonorScreeningInProgress;
	}>(ktxQueries.liveDonorScreening);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(
		() =>
			value === undefined
				? ""
				: formatMessage(
						liveDonorScreeningInProgressMessages,
						value.liveDonorScreeningInProgress
					),
		[value, formatMessage]
	);
}

function usePreviousKidneyTransplantationDates(): string[] {
	const events = useEventsByQuery<PreviousOtherOrganTransplantationType>(
		ktxQueries.previousOtherOrganTransplantation
	);
	const { formatDate } = useIntl();
	return React.useMemo(
		() =>
			events
				.filter(
					({ value }) =>
						value ===
						PreviousOtherOrganTransplantationType.PREVIOUS_TRANSPLANTATION_KIDNEY
				)
				.slice(0, 3)
				.map(({ timestamp }) =>
					formatDate(timestamp, formatDateOptions)
				),
		[events, formatDate]
	);
}

function useEdta(): string {
	const value = useLastEventValueByQuery<{
		diagnosisBaselineDisease: DiagnosisBaselineDisease;
	}>(ktxQueries.kidneyDiseaseEthiology);
	const formatMessage = useFormatMessageMap();
	return React.useMemo(() => {
		if (value === undefined) {
			return "";
		}
		const message = formatMessage(
			diagnosisBaselineDiseaseMessages,
			value.diagnosisBaselineDisease
		);
		const matches = message.match(/^\d+/);
		if (matches === null) {
			return "";
		}
		return matches[0];
	}, [value, formatMessage]);
}

function useRenalReplacementTherapyDate(): string {
	const event = useLastEventByQuery(ktxQueries.renalReplacementTherapy);
	const { formatDate } = useIntl();
	return React.useMemo(
		() =>
			event === undefined
				? ""
				: formatDate(event.timestamp, formatDateOptions),
		[event, formatDate]
	);
}

function useRenalReplacementTherapyType(): string {
	const value = useLastEventValueByQuery<RenalReplacementTherapyType>(
		ktxQueries.renalReplacementTherapy
	);
	const formatMessage = useFormatMessageMapWithoutWarning();
	return React.useMemo(
		() =>
			value === undefined
				? ""
				: formatMessage(renalReplacementTherapyTypeMessages, value),
		[value, formatMessage]
	);
}

function useCauseOfGraftloss(): string {
	const value = useLastEventValueByQuery<CauseOfGraftloss>(
		ktxQueries.graftloss
	);
	const formatMessage = useFormatMessageMapWithoutWarning();
	return React.useMemo(() => {
		const m = { ...causeOfGraftlossMessages, ...hasGraftlossMessages };
		return value === undefined ? "" : formatMessage(m, value);
	}, [value, formatMessage]);
}

function useGraftlossDate(): string {
	const event = useLastEventByQuery(ktxQueries.graftloss);
	const { formatDate } = useIntl();
	return React.useMemo(
		() =>
			event === undefined
				? ""
				: formatDate(event.timestamp, formatDateOptions),
		[event, formatDate]
	);
}
